import React, { useCallback, useEffect, useState } from 'react';
import { Undo2, Loader2, RefreshCcw, AlertTriangle, Mail } from 'lucide-react';
import api from '@/lib/axios';

export default function MarketplaceRefundsPanel() {
  const [data, setData] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null); // { order }
  const [form, setForm] = useState({ amount: '', motivo: '', confirm: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [r, o] = await Promise.all([
        api.get('/admin/marketplace/refunds'), api.get('/admin/marketplace/email-outbox'),
      ]);
      setData(r.data); setOutbox(o.data.items || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitRefund = async () => {
    if (!form.motivo.trim() || !form.confirm) return;
    setBusy(true);
    try {
      await api.post(`/admin/marketplace/orders/${modal.order.order_id}/refund`, {
        amount: form.amount ? Number(form.amount) : null, motivo: form.motivo.trim(), confirm: true,
      });
      setModal(null); setForm({ amount: '', motivo: '', confirm: false }); load();
    } catch (e) { setErr(e.response?.data?.detail || 'Falha no reembolso.'); }
    finally { setBusy(false); }
  };

  const Card = ({ label, value }) => (
    <div className="rounded-xl border border-slate-200 p-3 text-center">
      <p className="text-lg font-bold text-slate-900">{value} €</p>
      <p className="text-[10px] font-semibold uppercase text-slate-500">{label}</p>
    </div>
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="refunds-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <Undo2 className="h-5 w-5 text-emerald-600" /> Reembolsos
        </h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </div>
      {data?.modo === 'simulado' && <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700" data-testid="refunds-simulado">Comércio desativado — reembolsos em modo SIMULADO (não executa Stripe real).</p>}
      {err && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="refunds-error">{err}</p>}
      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
      ) : data && (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Card label="Pago" value={data.totais.pago} />
            <Card label="Reembolsado" value={data.totais.reembolsado} />
            <Card label="Ainda reembolsável" value={data.totais.ainda_reembolsavel} />
          </div>
          <ul className="mt-3 space-y-1.5" data-testid="refunds-orders">
            {data.encomendas.length === 0 && <li className="text-xs text-slate-500">Sem encomendas pagas.</li>}
            {data.encomendas.map((o) => (
              <li key={o.order_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[11px]">
                <span>
                  <span className="font-semibold text-slate-800">{o.order_number}</span>
                  <span className="text-slate-500"> · {o.amount}€ · reembolsado {o.amount_refunded}€ · reembolsável {o.refundable}€ · {o.estado_reembolso}</span>
                </span>
                {o.refundable > 0 && (
                  <button type="button" onClick={() => { setModal({ order: o }); setForm({ amount: '', motivo: '', confirm: false }); }}
                    className="rounded-lg bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold text-white" data-testid={`refund-btn-${o.order_id}`}>
                    Reembolsar
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <p className="inline-flex items-center gap-1 text-xs font-bold text-slate-700"><Mail className="h-3.5 w-3.5 text-emerald-600" /> Caixa de saída de emails ({outbox.length}) — {outbox[0]?.estado === 'enviado_real' ? 'envio real' : 'simulado'}</p>
            <ul className="mt-1 space-y-1" data-testid="email-outbox">
              {outbox.slice(0, 8).map((m) => (
                <li key={m.id} className="rounded border border-slate-200 px-2 py-1 text-[10px] text-slate-600">
                  <span className="font-semibold">{m.evento}</span> → {m.template_key}{m.fallback ? ' (fallback)' : ''} · {m.assunto} · <span className="text-amber-700">{m.estado}</span>
                </li>
              ))}
              {outbox.length === 0 && <li className="text-[10px] text-slate-500">Sem emails preparados.</li>}
            </ul>
          </div>
        </>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="refund-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
            <h3 className="text-sm font-bold text-slate-900">Reembolsar {modal.order.order_number}</h3>
            <p className="mt-1 text-[11px] text-slate-500">Reembolsável: {modal.order.refundable}€. Deixe o valor vazio para reembolso total.</p>
            <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder={`Valor (máx ${modal.order.refundable}€)`} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="refund-amount" />
            <textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} rows={2}
              placeholder="Motivo (obrigatório)" className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="refund-motivo" />
            <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-700">
              <input type="checkbox" checked={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.checked })} data-testid="refund-confirm" />
              <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Confirmo o reembolso (ação auditada).</span>
            </label>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancelar</button>
              <button type="button" onClick={submitRefund} disabled={busy || !form.motivo.trim() || !form.confirm}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="refund-submit">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirmar reembolso
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
